import React, { useState, useEffect } from "react";
import { Box, SxProps, Typography } from "@mui/material";
import {
  IPassengerTypesTranslations,
  IPassengerTypeShort,
  useConfig,
  formatPrice,
} from "@thekilroygroup/shared-ui";
import { useCountUp } from "use-count-up";

export const FarePriceLine = (props: {
  currency: string;
  taxAndSurchargesTitle: string;
  taxAndSurchargesAmount: number;
  priceTotal: number;
  passengerTypes: IPassengerTypesTranslations;
  passengerTypeShort: IPassengerTypeShort;
  passengerQuantity: number;
}) => {
  const {
    currency,
    passengerTypes,
    passengerTypeShort,
    passengerQuantity,
    priceTotal,
    taxAndSurchargesAmount,
    taxAndSurchargesTitle,
  } = props;

  const { locale } = useConfig();

  const getPassengerTypeLabel = (): string | undefined => {
    const translatedPassengerType =
      passengerTypes[passengerTypeShort as keyof typeof passengerTypes];
    return `${translatedPassengerType} x ${passengerQuantity}`;
  };

  /**
   * Logic for counting the lowest price total and lowest tax and surcharges START
   */
  const [lowestPriceTotal, setLowestPriceTotal] = useState(0);
  const [lowestTaxAndSurcharges, setLowestTaxAndSurcharges] = useState(0);

  const [localLowestPriceTotal, setLocalLowestPriceTotal] =
    useState(lowestPriceTotal);

  const [localLowestTaxAndSurcharges, setLocalLowestTaxAndSurcharges] =
    useState(lowestTaxAndSurcharges);

  useEffect(() => {
    setLowestPriceTotal(priceTotal);
    setLowestTaxAndSurcharges(taxAndSurchargesAmount);
  }, [priceTotal, taxAndSurchargesAmount]);

  useEffect(() => {
    countLowestTaxAndSurcharges();
    countLowestPriceTotal();
  }, [
    lowestPriceTotal,
    priceTotal,
    localLowestTaxAndSurcharges,
    taxAndSurchargesAmount,
  ]);

  const { value: animatedLowestPriceTotal, reset: countLowestPriceTotal } =
    useCountUp({
      isCounting: true,
      start: localLowestPriceTotal,
      end: lowestPriceTotal,
      duration: 0.4,
      onComplete: () => {
        setLocalLowestPriceTotal(lowestPriceTotal);
      },
    });
  const {
    value: animatedLowestTaxAndSurcharges,
    reset: countLowestTaxAndSurcharges,
  } = useCountUp({
    isCounting: true,
    start: localLowestTaxAndSurcharges,
    end: lowestTaxAndSurcharges,
    duration: 0.4,
    onComplete: () => {
      setLocalLowestTaxAndSurcharges(lowestTaxAndSurcharges);
    },
  });

  /**
   * Logic for counting the lowest price total and lowest tax and surcharges END
   */
  return (
    <Box
      sx={{
        mb: 1,
      }}
    >
      <Box sx={rowStyles}>
        <Typography variant="bodyMedium">{getPassengerTypeLabel()}</Typography>
        <Typography variant="titleMedium" weight="bold">
          {formatPrice(animatedLowestPriceTotal, locale, currency)}
        </Typography>
      </Box>
      <Box sx={rowStyles}>
        <Typography
          variant="titleSmall"
          weight="bold"
          pl={0.5}
          color="grey.600"
        >
          {taxAndSurchargesTitle}
        </Typography>
        <Typography variant="bodyMedium" color="grey.600">
          {formatPrice(animatedLowestTaxAndSurcharges, locale, currency)}
        </Typography>
      </Box>
    </Box>
  );
};

const rowStyles: SxProps = {
  display: "flex",
  flexWrap: "nowrap",
  justifyContent: "space-between",
  gap: 8,
};
